/*
* Copyright (c) 2009-2022. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

import Vue from 'vue';
import axios from 'axios';
import constants from '@/utils/constants.js';
import {resolveFromRealmList, UnknownRealmError} from '@/utils/tenant-realms.js';

import VueRouter from 'vue-router';
import router from './routes.js';
Vue.use(VueRouter);

import i18n from './lang.js';

import store from './store/store.js';

import Buefy from 'buefy';
Vue.use(Buefy, {defaultIconPack: 'fas'});

import VeeValidate, {Validator} from 'vee-validate';
Validator.extend('positive', value => Number(value) > 0);
Vue.use(VeeValidate, {
  i18nRootKey: 'validations',
  i18n,
  inject: false
});

import Notifications from 'vue-notification';
Vue.use(Notifications);

import VTooltip from 'v-tooltip';
Vue.use(VTooltip);

import VueMoment from 'vue-moment';
const moment = require('moment');
Vue.use(VueMoment, {moment});

import VueShortKey from 'vue-shortkey';
Vue.use(VueShortKey, {
  prevent: [
    'input[type=text]',
    'input[type=password]',
    'input[type=search]',
    'input[type=email]',
    'textarea',
    '.ql-editor'
  ]
});

import VueHtml2Canvas from 'vue-html2canvas';

Vue.use(VueHtml2Canvas);

import * as vClickOutside from 'v-click-outside-x';
Vue.use(vClickOutside);

import VueLayers from 'vuelayers';
import ImsSource from './vuelayers-suppl/ims-source';
import RasterSource from './vuelayers-suppl/raster-source';
import TranslateInteraction from './vuelayers-suppl/translate-interaction';
import RotateInteraction from './vuelayers-suppl/rotate-interaction';
import ModifyInteraction from './vuelayers-suppl/modify-interaction';
import RescaleInteraction from './vuelayers-suppl/rescale-interaction';
Vue.use(VueLayers);
Vue.use(ImsSource);
Vue.use(RasterSource);
Vue.use(TranslateInteraction);
Vue.use(RotateInteraction);
Vue.use(ModifyInteraction);
Vue.use(RescaleInteraction);

import Chart from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartZoom from 'chartjs-plugin-zoom';
Chart.plugins.unregister(ChartZoom);
Chart.plugins.unregister(ChartDataLabels);
Chart.helpers.merge(Chart.defaults.global.plugins.datalabels, {
  anchor: 'end',
  align: 'end',
  offset: 5,
  clamp: true
});

import App from './App.vue';

Vue.config.productionTip = false;
Vue.prototype.$eventBus = new Vue();

function normalizeTenantValue(value) {
  const normalized = (value || '').trim().toLowerCase();
  return normalized || null;
}

function parseTenantAllowlist(value) {
  const csv = (value || '').toString();
  return new Set(
    csv
      .split(',')
      .map(item => normalizeTenantValue(item))
      .filter(Boolean)
  );
}

function parseTenantRealmMap(rawValue) {
  if (!rawValue) {
    return {};
  }

  if (typeof rawValue === 'object') {
    return Object.entries(rawValue).reduce((acc, [tenant, realm]) => {
      const normalizedTenant = normalizeTenantValue(tenant);
      const normalizedRealm = (realm || '').toString().trim();
      if (normalizedTenant && normalizedRealm) {
        acc[normalizedTenant] = normalizedRealm;
      }
      return acc;
    }, {});
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return {};
    }
    try {
      return parseTenantRealmMap(JSON.parse(trimmed));
    } catch (_error) {
      return {};
    }
  }

  return {};
}

// Legacy fallback: resolve from configuration.json constants. Used only if tenant-realms.json
// cannot be loaded (older deployments). Falls back to the default realm on an unknown hint.
function resolveTenantRealmContext(currentUrl) {
  const defaultTenant = normalizeTenantValue(constants.CYTOMINE_DEFAULT_REALM) || 'zaya';
  const allowlist = parseTenantAllowlist(constants.CYTOMINE_TENANT_ALLOWLIST);
  const realmMap = parseTenantRealmMap(constants.CYTOMINE_TENANT_REALM_MAP);

  // Prefer `realm`; accept legacy `kc_realm` so existing bookmarks/shared links still resolve.
  let selectedTenant = normalizeTenantValue(currentUrl.searchParams.get('realm') || currentUrl.searchParams.get('kc_realm')) || defaultTenant;
  if (allowlist.size > 0 && !allowlist.has(selectedTenant)) {
    selectedTenant = allowlist.has(defaultTenant) ? defaultTenant : allowlist.values().next().value;
  }

  if (!selectedTenant) {
    throw new Error('Unable to resolve tenant context for Ims login');
  }

  const selectedRealm = realmMap[selectedTenant] || selectedTenant;
  return {selectedTenant, selectedRealm};
}

// Minimal error page shown when the requested realm is unknown (no Vue app, no Keycloak init).
function showUnknownRealmPage(realm) {
  const el = document.getElementById('app') || document.body;
  el.innerHTML = `
    <div style="font-family: sans-serif; max-width: 36rem; margin: 4rem auto; text-align: center;">
      <h1 style="color:#b00020;">Unknown tenant</h1>
      <p>The requested workspace <code>${String(realm).replace(/[<>&]/g, '')}</code> is not configured.</p>
      <p>Please check your login link or contact your administrator.</p>
    </div>`;
}

// Minimal error page for a login that authenticated but could not be provisioned in Ims.
// Same shape as showUnknownRealmPage: no Vue app, no store, safe to call pre-mount.
// Reasons come from POST /ims-service/v1/ims/sync/self (spec 2026-07-31-standalone-identity-bootstrap).
function showBootstrapErrorPage(reason) {
  const tenant = String(constants.ACTIVE_TENANT || '').replace(/[<>&]/g, '');
  const messages = {
    // 409 — this user is not a tenant admin and nobody has bootstrapped the tenant yet.
    TENANT_NOT_BOOTSTRAPPED: {
      title: 'Workspace not set up yet',
      body: `The workspace <code>${tenant}</code> has not been initialised.
             An administrator must sign in once to finish setting it up before other users can use it.`
    },
    // 403 — tenant missing from IMS_ENABLED_TENANTS on ims-service (a configuration problem).
    IMS_FEATURE_DISABLED: {
      title: 'Ims is not enabled',
      body: `Ims is not enabled for the workspace <code>${tenant}</code>.
             Please contact your administrator.`
    },
    NO_IMS_ROLE: {
      title: 'No access to Ims',
      body: `Your account does not have access to Ims.
             Ask an administrator to grant you the USER role.`
    }
  };
  const {title, body} = messages[reason] || {
    title: 'Could not sign you in',
    body: 'Ims could not prepare your account. Please try again or contact your administrator.'
  };
  const el = document.getElementById('app') || document.body;
  el.innerHTML = `
    <div style="font-family: sans-serif; max-width: 36rem; margin: 4rem auto; text-align: center;">
      <h1 style="color:#b00020;">${title}</h1>
      <p>${body}</p>
    </div>`;
}

// Load configuration before initializing Keycloak
axios.get('configuration.json').then(response => {
  const settings = response.data;
  for (let i in settings) {
    if (Object.prototype.hasOwnProperty.call(constants, i)
      || i.includes('_NAMESPACE') || i.includes('_VERSION') || i.includes('_ENABLED')) {
      constants[i] = settings[i];
    }
  }

  // Fetch the authoritative realm list (tenant-realms.json), then init Keycloak. Falls back
  // to configuration.json constants only if the realm list cannot be loaded.
  axios
    .get('tenant-realms.json')
    .then(res => res.data)
    .catch(() => null)
    .then(realmList => {
      const currentUrl = new URL(window.location.href);
      let selectedTenant;
      let selectedRealm;
      try {
        if (realmList) {
          ({selectedTenant, selectedRealm} = resolveFromRealmList(realmList, currentUrl));
        } else {
          ({selectedTenant, selectedRealm} = resolveTenantRealmContext(currentUrl));
        }
      } catch (err) {
        if (err instanceof UnknownRealmError) {
          showUnknownRealmPage(err.realm);
          return;
        }
        throw err;
      }

      // Now import and initialize Keycloak with the resolved realm.
      import('./keycloak').then(module => {
        const Keycloak = module.default;
        const {setKeycloakContext} = module;

        constants.ACTIVE_TENANT = selectedTenant;
        setKeycloakContext({
          realm: selectedRealm,
          tenant: selectedTenant
        });

        Vue.use(Keycloak);

        // Default ON (the production setting, where the adapter probes session via an
        // iframe). Set KEYCLOAK_CHECK_LOGIN_IFRAME=false in local dev where the IdP and
        // the SPA are on different schemes (HTTP vs HTTPS) to avoid mixed-content blocks;
        // production keeps the default and gets cross-tab logout detection.
        const checkLoginIframe = constants.KEYCLOAK_CHECK_LOGIN_IFRAME !== false
          && String(constants.KEYCLOAK_CHECK_LOGIN_IFRAME).toLowerCase() !== 'false';
        const keycloakInitOptions = {
          onLoad: 'login-required',
          checkLoginIframe,
        };

        Vue.$keycloak
          .init(keycloakInitOptions)
          .then(async () => {
            // Ims Core identifies users by the reference `iss|sub` and never creates them, so a
            // freshly-authenticated user does not exist in Core yet: the app's first API call
            // (GET /api/user/current.json) would return 500 "Cannot find current user". sync/self
            // materializes the user (and bootstraps the tenant when the caller is a tenant admin).
            // LIS used to make this call; standalone Ims makes it here. Blocking on purpose — no
            // Core call may run before the user exists.
            try {
              const {default: imsService} = await import('@/api/pipeline-service.js');
              await imsService.syncSelf();
            } catch (err) {
              const status = err && err.response && err.response.status;
              const detail = err && err.response && err.response.data && err.response.data.detail;
              const code = detail && typeof detail === 'object' ? detail.code : detail;
              if (status === 409 && code === 'TENANT_NOT_BOOTSTRAPPED') {
                showBootstrapErrorPage('TENANT_NOT_BOOTSTRAPPED');
                return;
              }
              if (status === 403) {
                showBootstrapErrorPage(code === 'NO_IMS_ROLE' ? 'NO_IMS_ROLE' : 'IMS_FEATURE_DISABLED');
                return;
              }
              // Transient (network error, 5xx, ims-service rolling): mount anyway rather than
              // white-screening the app. Core calls may still fail, which surfaces normally.
              console.error('sync/self failed; mounting anyway', err);
            }
            new Vue({
              render: h => h(App),
              router,
              store,
              i18n
            }).$mount('#app');
          });
      });
    });
});
